import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Mail } from 'lucide-react'
import { supabase, type FeaturedJob } from '../lib/supabase'

interface CompanyData {
    slug: string
    jobCount: number
}

interface GroupedCompany {
    companyName: string
    logo_url: string | null
    hm_photo: string | null
    hiring_manager: string | null
    hm_role: string | null
    jobs: Array<{
        job_title: string
        job_url: string
        badges: string[] | null
        hm_blurb: string | null
        listed: boolean | null
    }>
}

function Featured() {
    const navigate = useNavigate()
    const [groupedCompanies, setGroupedCompanies] = useState<GroupedCompany[]>([])
    const [companyData, setCompanyData] = useState<Record<string, CompanyData>>({})
    const [isLoading, setIsLoading] = useState(true)

    // Function to group jobs by company
    function groupJobsByCompany(jobs: FeaturedJob[]): GroupedCompany[] {
        const companyMap = new Map<string, GroupedCompany>()

        jobs.forEach(job => {
            const companyName = job.name || job.company_name

            if (!companyMap.has(companyName)) {
                // Create new company entry
                companyMap.set(companyName, {
                    companyName,
                    logo_url: job.logo_url,
                    hm_photo: job.hm_photo,
                    hiring_manager: job.hiring_manager,
                    hm_role: job.hm_role,
                    jobs: []
                })
            }

            // Add job to company's job list with its own hm_blurb
            const company = companyMap.get(companyName)!
            company.jobs.push({
                job_title: job.job_title,
                job_url: job.job_url,
                badges: job.badges,
                hm_blurb: job.hm_blurb,
                listed: job.listed
            })
        })

        return Array.from(companyMap.values())
    }

    useEffect(() => {
        async function fetchFeaturedJobs() {
            try {
                const { data, error } = await supabase
                    .from('featured')
                    .select('company_name, name, job_title, job_url, logo_url, hm_photo, hiring_manager, hm_role, hm_blurb, badges, listed')

                if (error) throw error

                // Group jobs by company
                const grouped = groupJobsByCompany(data || [])
                setGroupedCompanies(grouped)

                // Fetch company data (slug and job count) for each unique company
                if (data && data.length > 0) {
                    const companyDataMap: Record<string, CompanyData> = {}
                    const uniqueCompanies = new Set(data.map(job => job.name || job.company_name))

                    for (const companyName of uniqueCompanies) {
                        // Get company slug
                        const { data: companyInfo } = await supabase
                            .from('companies')
                            .select('slug')
                            .eq('name', companyName)
                            .maybeSingle()

                        if (companyInfo?.slug) {
                            // Count active jobs for this company
                            const { count } = await supabase
                                .from('raw_jobs')
                                .select('*', { count: 'exact', head: true })
                                .eq('company_name', companyInfo.slug)
                                .eq('is_active', true)

                            companyDataMap[companyName] = {
                                slug: companyInfo.slug,
                                jobCount: count || 0
                            }
                        }
                    }

                    setCompanyData(companyDataMap)
                }
            } catch (error) {
                console.error('Error fetching featured jobs:', error)
            } finally {
                setIsLoading(false)
            }
        }

        fetchFeaturedJobs()
    }, [])

    function getCompanyInitials(companyName: string): string {
        const words = companyName.trim().split(/\s+/)
        if (words.length === 1) {
            return words[0].substring(0, 2).toUpperCase()
        }
        return (words[0][0] + words[1][0]).toUpperCase()
    }

    if (isLoading) {
        return (
            <div className="bg-white pb-8">
                <div className="max-w-4xl mx-auto px-3 sm:px-4 py-16">
                    <div className="mb-8">
                        <h1 className="text-2xl font-medium text-gray-900 mb-2">
                            Featured Jobs
                        </h1>
                        <p className="text-sm text-gray-600">
                            Curated opportunities from top companies.
                        </p>
                    </div>
                    <div className="space-y-6">
                        {[...Array(3)].map((_, i) => (
                            <div
                                key={i}
                                className="p-6 rounded-xl border border-gray-200 bg-white animate-pulse"
                            >
                                <div className="h-32 bg-gray-100 rounded"></div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )
    }

    if (groupedCompanies.length === 0) {
        return (
            <div className="bg-white pb-8">
                <div className="max-w-4xl mx-auto px-3 sm:px-4 py-16">
                    <div className="mb-8">
                        <h1 className="text-2xl font-medium text-gray-900 mb-2">
                            Featured Jobs
                        </h1>
                        <p className="text-sm text-gray-600">
                            Curated opportunities from top companies.
                        </p>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center text-gray-600">
                        No featured jobs available yet. Check back soon!
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="bg-white pb-8">
            <div className="max-w-4xl mx-auto px-3 sm:px-4 py-16">
                <div className="mb-8">
                    <h1 className="text-2xl font-medium text-gray-900 mb-2">
                        Featured Jobs
                    </h1>
                    <p className="text-sm text-gray-600">
                        Curated opportunities from top companies with direct access to hiring managers.
                    </p>
                </div>

                <div className="space-y-6">
                    {groupedCompanies.map((company, index) => (
                        <div
                            key={index}
                            className="group relative p-6 rounded-xl bg-white transition-all duration-300"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6">
                                {/* Left Column: Hiring Manager */}
                                <div className="flex flex-col items-center text-center space-y-3">
                                    {/* Hiring Manager Photo */}
                                    {company.hm_photo ? (
                                        <img
                                            src={company.hm_photo}
                                            alt={company.hiring_manager || 'Hiring Manager'}
                                            className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-md"
                                        />
                                    ) : (
                                        <div className="w-24 h-24 rounded-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-500 text-white text-2xl font-semibold border-4 border-white shadow-md">
                                            {company.hiring_manager ? getCompanyInitials(company.hiring_manager) : '?'}
                                        </div>
                                    )}

                                    {/* Hiring Manager Info */}
                                    <div>
                                        {company.hiring_manager && (
                                            <h3 className="font-semibold text-gray-900 text-sm">
                                                {company.hiring_manager}
                                            </h3>
                                        )}
                                        {company.hm_role && (
                                            <p className="text-xs text-gray-600">
                                                {company.hm_role}
                                            </p>
                                        )}
                                    </div>

                                    <div className="pt-2">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                // TODO: Open intro modal/form
                                                console.log('Get intro clicked for:', company.hiring_manager)
                                            }}
                                            className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium hover:bg-blue-200 hover:scale-105 active:scale-95 transition-all duration-200 cursor-pointer"
                                        >
                                            <Mail className="w-3 h-3" />
                                            Get an Intro
                                        </button>
                                    </div>
                                </div>

                                {/* Right Column: Company & Jobs */}
                                <div className="flex flex-col space-y-4">
                                    {/* Company Header */}
                                    <div className="flex items-start gap-3">
                                        {company.logo_url ? (
                                            <img
                                                src={company.logo_url}
                                                alt={company.companyName}
                                                className="w-16 h-16 rounded-lg object-contain bg-white border border-gray-200 shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    const companyInfo = companyData[company.companyName]
                                                    if (companyInfo?.slug) {
                                                        navigate(`/company/${encodeURIComponent(companyInfo.slug)}`)
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <div
                                                className="w-16 h-16 rounded-lg flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-500 text-white text-base font-semibold shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    const companyInfo = companyData[company.companyName]
                                                    if (companyInfo?.slug) {
                                                        navigate(`/company/${encodeURIComponent(companyInfo.slug)}`)
                                                    }
                                                }}
                                            >
                                                {getCompanyInitials(company.companyName)}
                                            </div>
                                        )}

                                        <div className="flex-1 min-w-0">
                                            <div>
                                                <p
                                                    className="text-lg font-semibold text-gray-900 cursor-pointer hover:text-gray-700 transition-colors inline"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        const companyInfo = companyData[company.companyName]
                                                        if (companyInfo?.slug) {
                                                            navigate(`/company/${encodeURIComponent(companyInfo.slug)}`)
                                                        }
                                                    }}
                                                >
                                                    {company.companyName}
                                                </p>
                                                {companyData[company.companyName]?.jobCount !== undefined && (
                                                    <span className="text-xs text-gray-500 ml-2">
                                                        ({companyData[company.companyName].jobCount} open {companyData[company.companyName].jobCount === 1 ? 'job' : 'jobs'})
                                                    </span>
                                                )}
                                            </div>

                                            {/* Badges - Collect unique badges from all jobs */}
                                            {(() => {
                                                const allBadges = company.jobs.flatMap(job => job.badges || [])
                                                const uniqueBadges = [...new Set(allBadges)]
                                                return uniqueBadges.length > 0 && (
                                                    <div className="flex flex-wrap gap-2 mt-2">
                                                        {uniqueBadges.map((badge, badgeIndex) => (
                                                            <span
                                                                key={badgeIndex}
                                                                className="inline-flex items-center px-2.5 py-1 rounded-md bg-gray-100 text-gray-700 text-xs font-medium"
                                                            >
                                                                {badge}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )
                                            })()}
                                        </div>
                                    </div>

                                    {/* Job Listings */}
                                    <div className="space-y-3 pt-2">
                                        {company.jobs.map((job, jobIndex) => (
                                            <div key={jobIndex} className="border-l-2 border-blue-200 pl-4 space-y-2">
                                                <div className="flex items-start gap-2">
                                                    <a
                                                        href={job.job_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-base font-medium text-gray-900 hover:text-blue-600 transition-colors"
                                                    >
                                                        {job.job_title}
                                                    </a>
                                                    {job.listed !== null && (
                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap ${
                                                            job.listed
                                                                ? 'bg-green-100 text-green-700'
                                                                : 'bg-red-100 text-red-700'
                                                        }`}>
                                                            {job.listed ? 'in careers page' : 'referral only'}
                                                        </span>
                                                    )}
                                                </div>
                                                {/* Hiring Manager's Personal Pitch */}
                                                {job.hm_blurb && (
                                                    <p className="text-sm text-gray-600">
                                                        {job.hm_blurb}
                                                    </p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default Featured
